/**
 * Which ProGlove model a device is, from the Streams API `device_model` or,
 * before a scan has delivered one, the serial prefix (see README.md). Pure, so
 * non-UI code (e.g. the display class) can use it without the artwork.
 */

export type ScannerModelVariant = 'mark-basic' | 'mark-2' | 'mark-3' | 'mai' | 'mark-display' | 'leo' | 'generic';

/**
 * Maps the Streams API `device_model` to a known scanner model, falling back to
 * the serial prefix while no scan has delivered a model yet (see README.md).
 * Model matching is intentionally fuzzy because the real `device_model` values
 * are undocumented; anything unknown becomes the generic variant.
 */
export const resolveScannerModelVariant = (deviceModel?: string, deviceSerial?: string): ScannerModelVariant => {
  // The first two letters are the family, the rest only the range (README.md).
  const serial = (deviceSerial ?? '').toUpperCase();
  if (serial.startsWith('MB')) {
    return 'mark-basic';
  }
  if (serial.startsWith('MD')) {
    return 'mark-display';
  }

  const source = `${deviceModel ?? ''} ${deviceSerial ?? ''}`.toUpperCase();

  if (/\bMAI/.test(source)) {
    return 'mai';
  }
  if (source.includes('DISPLAY')) {
    return 'mark-display';
  }
  // the official Streams API device_model values spell the number out ("Mark Three")
  if (source.includes('MARK 3') || source.includes('MARK3') || source.includes('MARK THREE') || /\bM3/.test(source)) {
    return 'mark-3';
  }
  if (source.includes('MARK 2') || source.includes('MARK2') || source.includes('MARK TWO') || /\bM2/.test(source)) {
    return 'mark-2';
  }
  if (source.includes('LEO') || /\bL[12]/.test(source)) {
    return 'leo';
  }
  if (source.includes('BASIC')) {
    return 'mark-basic';
  }

  return 'generic';
};

export const variantLabel = (variant: ScannerModelVariant, deviceModel?: string): string => {
  switch (variant) {
    case 'mark-basic':
      return 'MARK Basic';
    case 'mark-2':
      return 'MARK 2';
    case 'mark-3':
      return 'MARK 3';
    case 'mai':
      return 'MAI';
    case 'mark-display':
      return 'MARK Display';
    case 'leo':
      return 'LEO';
    default:
      return deviceModel ?? 'ProGlove Device';
  }
};

export const scannerModelLabel = (deviceModel?: string, deviceSerial?: string): string => {
  return variantLabel(resolveScannerModelVariant(deviceModel, deviceSerial), deviceModel);
};
